import { action, computed, observable } from 'mobx';
import stores from '../../../models/Stores';
import sleep from 'cd-eft/utils/sleep';
import {
  MapView,
  GraphicsLayer,
  Sketch,
  GroupLayer,
  RasterData,
  FeatureSet,
  esriProjection,
  Geometry,
  esriGeometryEngine,
  SpatialReference,
  Query,
  ImageryLayer,
  FeatureLayer,
} from 'cd-eft/utils/esriTypes';
import Search from '@arcgis/core/widgets/Search';
import Point from '@arcgis/core/geometry/Point';
import MessageCenter from 'cd-eft/components/common/MessageCenter/MessageCenterVM';
import Layer from '@arcgis/core/layers/Layer';
import * as gisUtils from 'cd-eft/utils/gisUtils';
import esriRequest from '@arcgis/core/request';
import esriConfig from '@arcgis/core/config';
import Polygon from '@arcgis/core/geometry/Polygon';
import { appConfig } from 'cd-eft/config/AppConfig';
import { DelineateWatershedInputStateVM } from 'cd-eft/components/EFH2/DelineateWatershedInput/DelineateWatershedInputStateVM';
import { DesignContainerVM } from 'cd-eft/components/Design/DesignContainer/DesignContainerVM';
import { projectClassId } from 'cd-eft/components/Projects/ProjectContainer/ProjectContainerVM';
import * as SETAPI from 'cd-eft/services/eftSetApi';
import { noSpecialChars } from 'cd-eft/utils';
import DemSurfaceGpApi, { IDemSurfaceUploadRequestParams } from 'cd-eft/services/DemSurfaceGpApi/DemSurfaceGpApi';
import Viewpoint from '@arcgis/core/Viewpoint';
import * as PRJAPI from 'cd-eft/services/eftPrjAdminApi';
import { SpatialRefRequestDto, StateCountyDto } from 'cd-eft/_codegen_/openapi-generator/eft-prjadmin/api';
import Extent from '@arcgis/core/geometry/Extent';
import { getProjectionByLocation, getSpatialRefByProject } from 'cd-eft/components/Projects/ProjectsLibrary/ProjectsLibraryVM';
import { AppConstants } from 'cd-eft/appConstants';
import { saveScreenshot } from 'cd-eft/utils/gisUtils';
import { EngineeringMapService } from 'cd-eft/framework/mapServices/EngineeringMapServices';
import { ClippedContainerVM } from 'cd-eft/components/EFH2/ClippedDEM/ClippedContainerVM';
import { validateDemFile } from 'cd-eft/utils/fileUploadUtils';

const designController = SETAPI.DesignElementController;

// DEM export parameters
const demClipMaxSize: number = 3200000; // 4000000; // no more than 4 million pixels (3.2 million now)
const demServiceTimeout: number = 120000; // 120 seconds for now
const demIdentifyTimeout: number = 20000; // 20 seconds for now
const __minScreenshotSize = 500;

//NOTE: visual draw order
const ___resultLayerNames = [
  'result_dem_layer',
  'result_dem_boundary_layer',
  'result_clipdem_layer',
];

//NOTE: visual draw order
const ___resultNames = [
  'result_dem',
  'result_dem_boundary',
  'result_clipdem',
  'result_depthgrid',
];

const aoiGraphicSymbol = {
  type: 'simple-fill', // autocasts as new esri.SimpleFillSymbol()
  color: [0, 0, 0, 0],
  style: 'none',
  outline: {
    // autocasts as new esri.SimpleLineSymbol()
    color: 'red',
    width: 2,
  },
} as any;

const __notAvailableSuffix: string = ' NOT AVAILABLE';
const setStore = stores.AppStore.surveyEngineeringStore;
const designStore = stores.AppStore.designStore;
const libraryStore = stores.AppStore.projectsLibraryStore;

type GpLinearUnit = 'feet' | 'meters';

export class DemIntegrationVM {
  @observable jobMessages: string[] = [];
  @observable gpSessionId: string = '';
  private project_class_id: projectClassId;
  private demProjectVM: DelineateWatershedInputStateVM | DesignContainerVM | ClippedContainerVM;
  public readonly mapView: MapView;
  public readonly aoiSketch: Sketch;

  private errorDemServices: string[] = [];
  private demFileCellSize?: number = undefined;
  private bestAvailableHillshade: ImageryLayer | undefined;
  private configDemServices: any[] = [];
  private readonly demServicesProperties: any[] = [];
  public readonly searchBar: Search;
  private in_dem_service_url: string = '';
  private in_dem_metadata_url: string = '';
  private suppressDemServiceLoading = false;
  private aoiModified: boolean = false;
  private readonly demServicesGroupLayer: GroupLayer;
  @observable saveButtonDisabled: boolean = true;

  @observable jobExecuting: boolean = false;
  @observable in_dem_hillshade: string = '';
  @observable demMetadataValues: any = [];
  @observable isDemMetadataWindowVisible: boolean = false;
  @observable in_dem_id: string = '';
  @observable in_dem_title: string = '';
  @observable in_dem_service_selection: 'service' | 'upload' | undefined = undefined;
  @observable in_dem_file: File | undefined = undefined;
  @observable in_dem_elevation_units: string = '';
  @observable in_dem_clip_url: string = '';
  @observable selected_dem_service_url: string | undefined;
  @observable in_generate_contours: boolean = true;
  @observable in_contour_interval: number = 2.0;
  @observable in_generate_hillshade: boolean = true;
  @observable in_generate_depthgrid: boolean = false;
  @observable in_generate_streamlines: boolean = true;
  @observable in_stream_threshold: number = 2.0;
  @observable aoiArea: string | undefined;
  @observable public isDemServiceAvailable: boolean = false;
  @observable public availableDemServices: IAvailableDemService[] = [];

  @observable suppressNaipServiceLoading = false;
  private configNaipService: string = '';
  @observable in_naip_selection: 'service' | undefined = undefined;
  @observable public isNaipServiceAvailable: boolean = false;
  @observable public availableNaipServices: any = {};

  public workingSpatialReference?: SpatialReference = undefined;
  private readonly in_projection: number = -1;
  @observable private readonly api: DemSurfaceGpApi = new DemSurfaceGpApi();
  private currentDemCellSize?: number = undefined;
  public readonly resultsGroupLayer: GroupLayer;
  @observable countyExtent?: Geometry;
  private demModified: boolean = false;
  private demNameEdited: boolean = false;
  private surfaceNameEdited: boolean = false;



  @observable result_dem: RasterData | undefined;
  @observable result_clipdem: RasterData | undefined;
  @observable result_hillshade: RasterData | undefined;
  @observable result_depthgrid: RasterData | undefined;
  @observable result_dem_boundary: FeatureSet | undefined;
  @observable result_dem_layer: Layer | undefined;
  @observable result_aoi: FeatureSet | undefined;
  //result_sessionId
  @observable result_sessionId: string = "";

  @observable error: boolean = false;


  //DEM Integration-For Save Functionality

  @observable formValidation = {
    //TODOs: In future, update the validation field if more fields are added
    formErrors: {
      demName: '',
      surfaceName: '',
      coordinateSystemType: '',
      zone: ''
    },
    demNameValid: false,
    surfaceNameValid: false,
    coordinateSystemTypeValid: false,
    zoneValid: false,
    formValid: false,
  };

  @observable newDEM = {
    demName: setStore.savedDEM ? setStore.currentDEM.demName : '',
    surfaceName: designStore.currentSurface.surfaceDefTitle,
    coordinateSystemType: libraryStore.projectSpatialRefData.coordinateType ? libraryStore.projectSpatialRefData.coordinateType : '',
    zone: libraryStore.projectSpatialRefData.zone ? libraryStore.projectSpatialRefData.zone : ''
  };

  private watchHandle1;
  private watchHandle2;
  private watchHandle3;
  private watchHandle4;


  constructor(
    mapView: MapView,
    project_class_id: projectClassId,
    demProjectVM: DelineateWatershedInputStateVM | DesignContainerVM | ClippedContainerVM,
    gpSessionId?: string,
  ) {

    this.mapView = mapView;
    this.demProjectVM = demProjectVM;
    this.project_class_id = project_class_id;
    this.aoiSketch = demProjectVM?.aoiSketch;
    this.demServicesGroupLayer = demProjectVM?.demServicesGroupLayer;
    this.resultsGroupLayer = demProjectVM?.resultsGroupLayer;

    this.in_dem_file = demProjectVM?.in_dem_file;
    this.in_dem_elevation_units = demProjectVM?.in_dem_elevation_units;



    if (demProjectVM instanceof DelineateWatershedInputStateVM) {
      this.in_generate_contours = demProjectVM.in_generate_contours;
      this.in_contour_interval = designStore.contourInterval;
      this.in_generate_hillshade = demProjectVM.in_generate_hillshade;
      this.in_generate_depthgrid = demProjectVM.in_generate_depthgrid;
      this.in_generate_streamlines = demProjectVM.in_generate_streamlines;
      this.in_stream_threshold = demProjectVM.in_stream_threshold;
    }

    if (this.aoiSketch !== undefined) {
      this.aoiSketch.viewModel.polygonSymbol = aoiGraphicSymbol;
    }
    this.watchHandle1 = this.aoiSketch?.on('update', this.aoiSketchUpdateHandler.bind(this));
    this.watchHandle2 = this.aoiSketch?.on('create', async (event) => {
      if (event.state === 'active') {
        if (event.graphic !== null) {
          const area = esriGeometryEngine.geodesicArea(event.graphic.geometry as Polygon, "acres");
          this.aoiArea = area.toFixed(2);
        }
      }

      if (event.state === 'complete') {
        const currentAoi = event.graphic;
        const lyr = this.aoiSketch.layer as GraphicsLayer;
        if (lyr) {
          if (lyr.graphics.length > 1) {
            const previousAoi = lyr.graphics.getItemAt(0);
            lyr.remove(previousAoi);
          }
          if (this.in_dem_service_selection === 'service') {
            await this.checkDemServicesAvailability();
          }
        }
      }
    });

    this.watchHandle3 = this.aoiSketch?.watch('activeTool', () => {
      if (this.demProjectVM instanceof DelineateWatershedInputStateVM) {
        this.demProjectVM.activeToolWatchHandler.bind(this)
      }
    });

    this.watchHandle4 = this.aoiSketch?.on('delete', () => {
      this.aoiArea = undefined;
      this.disableServiceDems();

      if (this.in_dem_service_selection === 'service') {
        this.clearAvailableDemServices();
      }
    })

    if (gpSessionId) this.gpSessionId = gpSessionId;
    this.searchBar = new Search({
      view: this.mapView,
      id: 'searchWidget',
    });

    this.mountLayers();

    this.configDemServices = appConfig.demMapServices;
    this.configNaipService = appConfig.usdaNaipSources.nonTiledPath;
    if (stores.AppStore.userProfileStore.applicationFlags.enabledemsearch) {
      if (demProjectVM instanceof DelineateWatershedInputStateVM) {
        if (demProjectVM.currentStepIndex === 0) {
          this.createDemImageLayers();
        }
      }
      else {
        this.createDemImageLayers();
      }
    };
  } // end constructor

  @action.bound setDEMName(event: any) {
    //validate the DEM surface name input, make sure it is not empty
    const { name, value } = event.target;

    this.newDEM[name] = value;
    this.validateField(name, value);
    this.newDEM.demName = value;
    setStore.currentDEM.demName = this.newDEM.demName;
    if (setStore.currentDEM.demSurfaceName) {
      this.demNameEdited = true;
      this.saveButtonDisabled = false;
    }
  }

  @action.bound setSurfaceName(event: any) {
    const { name, value } = event.target;
    this.newDEM[name] = value;
    this.validateField(name, value);
    this.newDEM.surfaceName = value;
    designStore.currentSurface.surfaceDefTitle = this.newDEM.surfaceName;
    if (setStore.currentDEM.demSurfaceName) {
      this.surfaceNameEdited = true;
      this.saveButtonDisabled = false;
    }
  }

  @action.bound async updateSurfaceName() {
    const postJSON = {
      projectUuid: designStore.projectsStore.currentProjectId,
      status: 'update',
      surfaceName: designStore.currentSurface.surfaceDefTitle,
    };

    try {
      await SETAPI.SurfaceDefController.updateSurfaceNameByProjectUuidUsingPOST(postJSON);
    } catch (error) {
      MessageCenter.addError((error as Error).message);
    }
  }
  @action.bound async updateContourInterval() {
    try {
      await SETAPI.DesignElementController.updateDEMSurfaceByProjectUuidAndNewIntervalUsingPOST(designStore.projectsStore.currentProject.projectUuid, designStore.contourInterval);
      setStore.isContourIntervalEdited = false;
    } catch (error) {
      MessageCenter.addError((error as Error).message);
    }
  }

  @action.bound handleCoordinateSystemChange(event) {
    event.preventDefault();
    if (designStore.hasProject() || setStore.currentTab === 'Clipped DEM') {
      const { name, value } = event.target;
      this.newDEM[name] = value
      this.validateField(name, value);
    }
  }

  @action.bound handleZoneInputChange(event) {
    event.preventDefault();
    const { name, value } = event.target;
    this.newDEM[name] = value
    this.validateField(name, value);
    //update spatial reference
    this.createSpatialRef();
  }

  @action.bound async projectionByLocation() {
    libraryStore.pojectionByLocationData = []
    if (this.newDEM.coordinateSystemType !== '') {
      await getProjectionByLocation(this.newDEM.coordinateSystemType, designStore.projectsStore.currentProject.state, designStore.projectsStore.currentProject.county)
      if (!libraryStore.projectSpatialRefData.zone && libraryStore.pojectionByLocationData.length > 1) {
        this.newDEM.zone = ''
      }
      else {
        this.newDEM.zone = libraryStore.pojectionByLocationData.zoneName
        //update spatial reference
        this.createSpatialRef();
      }
    }
  }


  @action.bound createSpatialRef = async () => {
    //update the spatial reference objects if user added/redefined the projection in DEM
    const zoneValue = (this.newDEM.zone && this.newDEM.zone !== '') ? this.newDEM.zone : libraryStore.projectSpatialRefData.zone;
    let projectionName = libraryStore.pojectionByLocationData.length === 1
      ? libraryStore.pojectionByLocationData[0].projectionName
      : libraryStore.pojectionByLocationData.find((data) => data.zoneName === this.newDEM.zone)?.projectionName || '';
    const shouldAddFtIntl = !projectionName.includes('_Ft_Intl');
    projectionName = shouldAddFtIntl ? `${projectionName}_Ft_Intl` : projectionName;
    projectionName = projectionName.includes("Ft_Intl") ? projectionName : `${projectionName}_Ft_Intl`;
    if (libraryStore.projectSpatialRefData.coordinateType !== this.newDEM.coordinateSystemType || libraryStore.projectSpatialRefData.zone !== this.newDEM.zone) {
      const postJSON: SpatialRefRequestDto = {
        coordinateType: this.newDEM.coordinateSystemType,
        createdBy: String(localStorage['username']),
        espg: libraryStore.pojectionByLocationData[0]?.epsgCode,
        projectUuid: designStore.projectsStore.currentProjectId,
        projectionName: (this.newDEM.zone && this.newDEM.zone !== '') || (this.newDEM.coordinateSystemType !== libraryStore.projectSpatialRefData.coordinateType) ? projectionName : libraryStore.projectSpatialRefData.projectionName,
        unit: 'Feet',
        wkid: libraryStore.pojectionByLocationData[0]?.esriWkid,
        zone: zoneValue,
      };

      libraryStore.projectSpatialRefData.coordinateType = this.newDEM.coordinateSystemType;
      libraryStore.projectSpatialRefData.zone = zoneValue;

      // Check if any of the payload properties are undefined or empty
      if (Object.values(postJSON).some(value => value === undefined || value === '')) {
        return; // Stop the function execution
      }
      try {
        const response = await PRJAPI.SpatialRefController.createSpatialRefUsingPOST(postJSON);
        if (response.data.wkid !== null) {
          libraryStore.projectSpatialReference = new SpatialReference({ wkid: Number(response.data.wkid) });
        } else if (response.data.wktJson !== null) {
          const wktString = response.data.wktJson;
          libraryStore.projectSpatialReference = new SpatialReference({ wkt: wktString });
        }
      } catch (error) {
        MessageCenter.addError((error as Error).message);
      }

    }
  }


  //onClickSaveDEM
  @action.bound onClickSaveDEM = async () => {
    designStore.isLoading = true;
    try {
      //TODOs:  Need update drawings on map.
      //make sure the current form is valid
      if (!this.formValidation.formValid) {
        for (const field in this.newDEM) {
          const value = this.newDEM[field];
          await this.validateField(field, value);
        }
        if (!this.formValidation.formValid) {
          designStore.isLoading = false;
          return;
        }
      }
      if (!setStore.isImportingNewDEM && (this.surfaceNameEdited || this.demNameEdited || setStore.isContourIntervalEdited)) {
        if (this.demNameEdited) await designController.updateDEMSurfaceNameByProjectUuidAndNewDEMSurfaceNameUsingPOST(designStore.projectsStore.currentProject.projectUuid, setStore.currentDEM.demName);
        if (this.surfaceNameEdited) await this.updateSurfaceName();
        if (setStore.isContourIntervalEdited) await this.updateContourInterval();
      } else if (this.in_dem_service_selection === "service") {
        removeOriginalDEMLayers();
        const clipStatus = await this.demClip();
        if (!clipStatus) {
          designStore.isLoading = false;
          return; //Clip the image failed
        }
        designStore.setLoader('Saving DEM...');
        this.error = false;
        try {
          if (designStore.projectsStore.currentProject.projectUuid === undefined || setStore.currentDEM.demName === undefined || this.in_dem_clip_url === undefined) {
            designStore.isLoading = false;
            return
          }

          const response = await designController.saveOnlineDEMByProjectUuidWithIntervalUsingPOST(
            designStore.projectsStore.currentProject.projectUuid,
            "meters",
            setStore.currentDEM.demName,
            designStore.contourInterval,
            this.in_dem_clip_url + ", " + this.selected_dem_service_url!,
          );
          if (response == undefined || response.data == undefined) {
            //save service fails
            designStore.setLoader('');
          }
          const oldInput = document.getElementById('inFile') as HTMLInputElement;
          if (oldInput) {
            oldInput.files = null;
            oldInput.value = '';
          }
          this.in_dem_file = undefined;
          this.demProjectVM.in_dem_file = undefined;
        } catch (error) {
          this.error = true;
          designStore.setLoader('');
          console.error('Error:', error);
          if ((error as any).response && (error as any).response.data && (error as any).response.data.errorMessage) {
            // DEM_03 - saveOnline DEM and online DEM validations
            const errorCode = (error as any).response.data.originalErrorCode
            await MessageCenter.addErrorFromCode(errorCode)
          } else {
            //DEM_06 - 504,502 etc. error - Gateway Error (browser at 1min)
            await MessageCenter.addErrorFromCode('DEM_06')
          }
          setStore.savedDDEM = false;
          designStore.isLoading = false;
          return;
        }
        designStore.setLoader('');
      }
      else if (this.in_dem_service_selection === "upload") {
        if (designStore.projectsStore.currentProject.projectUuid === undefined || setStore.currentDEM.demName === undefined || this.in_dem_elevation_units === undefined || this.in_dem_file == undefined) {
          designStore.isLoading = false;
          return
        }
        removeOriginalDEMLayers();
        //upload
        //update the gp session id and session resource type id in the backend
        this.updateStoredGpSessionIdInResources(this.result_sessionId);
        designStore.setLoader('Saving DEM...');
        try {
          //If there is already an existing one, BE will use the upload API to overwrite it.
          const response = await designController.uploadDEMByProjectUuidUsingPOST(
            designStore.projectsStore.currentProject.projectUuid,
            this.in_dem_elevation_units,
            setStore.currentDEM.demName,
            designStore.contourInterval,
            setStore.upload_dem_file,
          );
          if (response == undefined || response.data == undefined) {
            //save service fails
            designStore.setLoader('');
          }
          this.createSpatialRef();
          getSpatialRefByProject(designStore.projectsStore.currentProjectId)
        } catch (error) {
          this.error = true;
          console.error('Error:', error);
          designStore.setLoader('');
          if ((error as any).response && (error as any).response.data && (error as any).response.data.errorMessage) {
            // DEM_02 user upload DEM and its validation
            await MessageCenter.addErrorFromCode('DEM_02')
          } else {
            MessageCenter.addError((error as Error).message);
          }
          setStore.savedDDEM = false;
          designStore.isLoading = false;

          return;
        }
        designStore.setLoader('');
      }
      setStore.in_designTypeSelection = 'demSurface';
      setStore.currentTab = 'Survey Summary';
      setStore.resetCurrentDEM();
      const graphic = this.demProjectVM.aoiGraphicsLayer.graphics.getItemAt(0);
      this.demProjectVM.aoiGraphicsLayer.remove(graphic);
      if (this.demProjectVM instanceof DesignContainerVM) {
        await DesignContainerVM.loadOriginalSurface();
      } else if (this.demProjectVM instanceof ClippedContainerVM) {
        await ClippedContainerVM.loadOriginalSurface();
      }
      await this.zoomToSubjectArea();
      await this.snapDEM();
      setTimeout(() => {
        const selectDEM = this.mapView.map.findLayerById('selectedDemLayer') as ImageryLayer;
        if (selectDEM) selectDEM.visible = false;
      }, 1000);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      designStore.isLoading = false;
    }
  };
  //cancel edit button onClick functionality
  @action.bound onClickCancelDEM = async () => {
    this.clearDemGraphics();
    this.demProjectVM.aoiSketch.delete();
    const lyr = this.aoiSketch.layer as GraphicsLayer;
    if (lyr) {
      if (lyr.graphics.length >= 1) {
        const previousAoi = lyr.graphics.getItemAt(0);
        lyr.remove(previousAoi);
      }
    }
    setStore.currentDEM.demName = '';
    setStore.currentDEM.demId = '';
    setStore.currentTab = 'Survey Summary';
    setStore.resetCurrentDEM();

    const originalDEMGroup = this.mapView.map.allLayers.filter((layer) => {
      return layer.title === 'Selected Dem Layer';
    });
    if (originalDEMGroup.length == 0) {
      await DesignContainerVM.loadOriginalSurface()
      await ClippedContainerVM.loadOriginalSurface()
    }
    ;
  };

  //for field and form validation
  @action.bound async validateField(fieldName, value) {
    // connects form field to validation table
    const keyValid = fieldName + 'Valid';
    let fieldValid = this.formValidation[keyValid];
    const fieldValidationErrors = this.formValidation.formErrors;
    let errorCode: string = '';
    /**
     * Survey_07: Survey Name is required and only numbers and letters are allowed.
     */
    switch (fieldName) {
      case 'demName':
        fieldValid = value !== '' && noSpecialChars(value);
        errorCode = fieldValid ? '' : 'Survey_07';
        break;
      case 'surfaceName':
        fieldValid = value !== '' && noSpecialChars(value);
        errorCode = fieldValid ? '' : 'Survey_24';
        break;
      case 'coordinateSystemType':
        fieldValid = !(value === '' || value === 0);
        errorCode = fieldValid ? '' : 'Common_08';      // value is required.
        if (fieldValid) {
          this.formValidation['zoneValid'] = true;
          await this.getErrorMessage('', this.formValidation.formErrors, 'zone');
        }
        break;
      case 'zone':
        // Skip validation if coordinate system type is valid
        if (this.formValidation['coordinateSystemTypeValid']) {
          break;
        }

        fieldValid = !(value === '' || value === 0);
        errorCode = fieldValid ? '' : 'Common_08';      // value is required.
        break;
      default:
        break;
    }
    this.formValidation.formErrors = fieldValidationErrors;
    await this.getErrorMessage(errorCode, this.formValidation.formErrors, fieldName);
    this.formValidation[keyValid] = fieldValid;
    this.validateForm();
  }

  @action.bound async getErrorMessage(code: string, container, fieldName: string) {
    if (code === '') {
      container[fieldName] = '';
    } else {
      let message = await MessageCenter.addErrorLabelFromCode(code);
      container[fieldName] = message;
      if (fieldName.includes('demName') && message != '') {
        message = message.replace('Survey', 'DEM');
        container[fieldName] = message;
      }
      let field;
      if (fieldName !== 'demName' && fieldName !== 'surfaceName') {
        field = fieldName
          .split(/(?=[A-Z])/)
          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ');
      }
      await MessageCenter.addNotificationFromCode(code, undefined, field);
    }
  }

  @action.bound validateForm() {
    let valid = true;
    for (const key in this.formValidation.formErrors) {
      if (!this.formValidation[key + 'Valid']) {
        valid = false;
      }
    }
    this.formValidation.formValid = valid;
  }

  @action.bound async createDemImageLayers() {
    this.jobExecuting = true;
    this.jobMessages = ['Loading DEM services...'];
    let message = '';
    this.errorDemServices = [];
    const removed = this.demServicesGroupLayer.removeAll();
    for (const lyr of removed) {
      lyr.destroy();
    }
    if (this.bestAvailableHillshade) this.mapView.map?.remove(this.bestAvailableHillshade);
    const currentTimeout = esriConfig.request.timeout;
    esriConfig.request.timeout = 15000;
    const maxTries = 10;
    let failed = true;
    let failedLayer: Layer | undefined;
    for (const svc of this.configDemServices) {
      let needToAdd = true;
      failedLayer = undefined;
      const foundLayer = this.demServicesGroupLayer.findLayerById(svc.id);
      if (foundLayer) {
        if (foundLayer.title.endsWith(__notAvailableSuffix)) {
          failedLayer = foundLayer;
        } else {
          needToAdd = false;
        }
      }
      if (needToAdd) {
        failed = true;
        message = message + `Loading ${svc.title}...`;
        this.jobMessages = [message];
        const startTime = Date.now();
        let imgLyr: ImageryLayer | undefined;
        for (let i = 1; ; i++) {
          try {
            imgLyr = new ImageryLayer({
              ...svc,
              visible: false,
              renderingRule: {
                functionName: svc.hillshadeName
              }
            });
            await imgLyr.load();

            const demServiceProps = await this.getDemServiceProperties(svc.url);
            if (demServiceProps && demServiceProps.spatialReference && demServiceProps.pixelSizeX) {
              this.demServicesProperties.push({
                ...demServiceProps,
                cellSize: DemIntegrationVM.getDemServiceCellSize(demServiceProps),
                id: svc.id as string,
                title: svc.title as string,
                url: svc.url as string
              });
            }
            if (failedLayer) {
              this.demServicesGroupLayer.remove(failedLayer);
              failedLayer.destroy();
              failedLayer = undefined;
            }
            this.demServicesGroupLayer.add(imgLyr);
            imgLyr = undefined;
            failed = false;
            break;
          } catch (error) {
            if (i >= maxTries) {
              if (imgLyr) {
                if (failedLayer) {
                  imgLyr.destroy();
                } else {
                  imgLyr.title = svc.title + __notAvailableSuffix;
                  this.demServicesGroupLayer.add(imgLyr);
                }
                imgLyr = undefined;
              }
              this.errorDemServices.push(svc.title);
              break;
            } else {
              if (imgLyr) {
                imgLyr.destroy();
                imgLyr = undefined;
              }
            }
          }
        }
        const elapsedTime = Math.ceil((Date.now() - startTime) / 1000.0);
        message = `${svc.title}\n${failed ? 'failed to load' : 'loaded'} in ${elapsedTime} second${elapsedTime > 1 ? 's' : ''}. `;
        this.jobMessages = [message];
        console.log(message);
      }
    }
    if (this.demServicesGroupLayer.findLayerById('usdaNedBestAvailable')) {
      this.bestAvailableHillshade = this.demServicesGroupLayer.findLayerById('usdaNedBestAvailable') as ImageryLayer;
      this.demServicesGroupLayer.remove(this.bestAvailableHillshade);

      const bestAvailableHillshadeLayer = this.mapView?.map?.findLayerById('usdaNedBestAvailable') as ImageryLayer;
      if (bestAvailableHillshadeLayer) this.mapView.map.remove(bestAvailableHillshadeLayer);
      this.mapView?.map?.add(this.bestAvailableHillshade, 0);
    }
    esriConfig.request.timeout = currentTimeout;
    this.jobExecuting = false;
    if (this.errorDemServices.length > 0 && setStore.currentTab != "Clipped DEM") {
      MessageCenter.addWarning('Failed to load the following DEM services:\n' + this.errorDemServices.join(',\n'));
    }
  }

  @action.bound setResultDem() {
    this.demProjectVM.result_dem = undefined;
  }

  @action.bound previewDem() {
    if (this.in_dem_id) {
      const selectedLayer = this.demServicesGroupLayer.findLayerById(this.in_dem_id);
      if (selectedLayer) {
        const graphic = this.demProjectVM.aoiGraphicsLayer.graphics.getItemAt(0);
        const newExent = graphic.geometry.extent;
        this.mapView.goTo(newExent);

        this.demServicesGroupLayer.layers.forEach((lyr) => (lyr.visible = false));
        selectedLayer.visible = true;
      }
    } else {
      MessageCenter.addWarning('Please select a DEM service to preview.');
    }
  }

  @action.bound async updateStoredGpSessionIdInResources(
    newGpSessionId: string
  ): Promise<void> {
    const sessionUuid = designStore.projectsStore.currentSessionId;
    const currentUser = localStorage['username'];

    let failedToUpdate = true;
    try {
      const response = await PRJAPI.EftSessionController.getSessionResourcesUsingGET(
        sessionUuid,
        1,
      );
      if (response) {
        if (0 < response.data.length) {
          const resource = response.data.find((item) => {
            return item;
          });

          if (resource) {
            const requestData = {
              currentUser: currentUser,
              sessionResourceID: resource.sessionResourceId,
              sessionResourceTypeId: 1,
              sessionResourceValue: newGpSessionId,
            };

            await PRJAPI.EftSessionController.updateSessionResourceUsingPOST(requestData);
            failedToUpdate = false;
          }
        }
      }
    } catch (error) {
      MessageCenter.addError((error as Error).message);
    } finally {
      if (failedToUpdate) {
        console.error('Failed to update stored GP session ID to ' + newGpSessionId);
      }
    }
  }


  @action.bound async validateDEMCRS() {
    try {
      const response = await SETAPI.DesignElementController.validateDEMCRSByProjectUuidUsingPOST(designStore.projectsStore.currentProjectId, this.in_dem_file);
      if (response.data.status === 'Projection_02') {
        await MessageCenter.addErrorFromCode('Projection_02')
        this.clearDemGraphics();
        return false;
      }
      return true;
    } catch (error) {
      if ((error as any).response && (error as any).response.data && (error as any).response.data.errorMessage) {
        // DEM_04 validateDEMCRSByProjectUuid any other unexpected exception
        await MessageCenter.addErrorFromCode('DEM_04')
      } else {
        MessageCenter.addError((error as any).message);
      }
      return false;
    }
  }

  // Extracted Parts of this.submit() in order to render the dem layer for an uploaded dem file.
  @action.bound async demSubmit(needHandleAoiSketch: boolean | undefined = true) {
    if (this.in_dem_file && !validateDemFile(this.in_dem_file)) {
      designStore.isLoading = false;
      return
    }
    this.demProjectVM.in_dem_elevation_units = this.in_dem_elevation_units;
    if (this.demProjectVM instanceof DelineateWatershedInputStateVM) {
      this.demProjectVM.demSubmit();
    }
    if (this.demProjectVM instanceof DesignContainerVM) {
      await this.demSubmitHelper();
    }
  }

  // Extracted Parts of this.submit() in order to render the dem layer for an uploaded dem file.
  @action.bound async demSubmitHelper(needHandleAoiSketch: boolean | undefined = true) {
    // Either file or elevation missing
    if (!(this.result_dem || this.in_dem_file) || !this.in_dem_elevation_units) {
      return;
    }
    // already have a dem loaded for some reason

    if (this.jobExecuting) {
      throw new Error('Invalid operation, the object is busy.');
    }

    if (!this.workingSpatialReference) {
      if (this.result_dem_boundary) {
        this.workingSpatialReference = this.result_dem_boundary.spatialReference.clone();
      } else {
        this.workingSpatialReference = this.mapView.spatialReference.clone();
      }
    }
    this.demFileCellSize = undefined;
    this.jobExecuting = true;
    this.jobMessages = [];

    //cancel any outstanding drawing inputs
    if (needHandleAoiSketch) {
      this.aoiSketch.cancel();
      await sleep(100); //introduced sleep to give sketch.cancel operation time to complete before disabling sketches,
      this.disableSketchControls();
    }

    try {
      const in_dem = this.result_dem || this.in_dem_file;
      const params00: IDemSurfaceUploadRequestParams = {
        in_server_folder: appConfig.gpSessionRoot,
        in_dem: in_dem,
        in_dem_elevation_units: this.in_dem_elevation_units,
      };

      const waitOptions: __esri.GeoprocessorWaitForJobCompletionOptions = {
        interval: 1000,
        signal: undefined,
        statusCallback: this.statusCallback,
      };
      if (!(await this.validateDEMCRS())) {
        return;
      }
      const results = await this.api.GenMapServiceFromDem(params00, waitOptions);

      if (results.result_dem) {
        this.result_dem = results.result_dem;
      }
      if (results.result_sessionId) {
        this.result_sessionId = results.result_sessionId;
        designStore.gpServiceSessionID = this.result_sessionId;
      }
      if (results.result_dem_boundary) {
        this.result_dem_boundary = results.result_dem_boundary;

        // update working spatialreference to match DEM for all future operations
        this.workingSpatialReference = this.result_dem_boundary.spatialReference.clone();
        if (this.result_dem_boundary.features.length > 0) {
          const feature = this.result_dem_boundary.features[0];
          if (feature !== undefined) {
            this.demFileCellSize = feature.attributes['CellSize'];
            const linearUnits = feature.attributes['LinearUnits'] as GpLinearUnit;
            if (this.demFileCellSize && linearUnits) {
              this.setCellSizeStreamThreshold(this.demFileCellSize, linearUnits);
            }
          }
        }
      }

      this.resultsGroupLayer.removeAll();

      for (const result_layer of ___resultLayerNames) {
        const layer = (results as any)[result_layer];
        if (layer) {
          this.resultsGroupLayer.add(layer);
          if (result_layer === "result_dem_layer") {
            this.result_dem_layer = layer;
          }
        }
      }
      await this.zoomToSubjectArea();
      this.demModified = true;
      this.saveButtonDisabled = false;
      setStore.isImportingNewDEM = true;
    } catch (e) {
      MessageCenter.addError(
        'Something went wrong while calling geoprocessing service to upload the DEM file. Please try again, possibly with different inputs. Error message: ' +
        (e as Error).message
      );
      this.saveButtonDisabled = true;
      setStore.isImportingNewDEM = false;
    } finally {
      this.jobExecuting = false;
      if (needHandleAoiSketch) {
        this.enableSketchControls();
      }
    }
  }

  private async snapDEM() {
    if (!this.mapView) return;

    const projectUuid = designStore.projectsStore.currentProjectId as string;
    if (projectUuid) {
      await this.mapView.when();
      await sleep(10000);
      // console.warn("ABOUT TO TAKE SHOT");
      const screenShot = await this.mapView?.takeScreenshot({ format: 'png', ignorePadding: true /*, layers */ });
      if (screenShot) {
        await saveScreenshot(projectUuid, screenShot.dataUrl);
      }
    }
  }

  public async zoomToSubjectArea(): Promise<void> {
    // TODO: update gp services to have consistent output names, then refactor this code
    const result_dem_layer = this.demServicesGroupLayer.findLayerById('result_dem_layer');
    const result_aoi_layer = this.demServicesGroupLayer.findLayerById('result_aoi');
    const result_dem_boundary_layer = this.demServicesGroupLayer.findLayerById('result_dem_boundary');

    //zoom to layer of interest, choose by descending order of interest
    let zoomLayer = this.result_dem_layer || result_dem_layer || result_aoi_layer || result_dem_boundary_layer;
    let useLayer: boolean = true;

    // use AOI graphics if about to export the DEM data from service
    if (
      zoomLayer === undefined &&
      this.in_dem_service_selection === 'service' &&
      this.demProjectVM.aoiGraphicsLayer.graphics.length > 0
    ) {
      zoomLayer = this.demProjectVM.aoiGraphicsLayer;
      useLayer = false;
    }
    if (zoomLayer) {
      //cannot access zoomLayer.fullExtent until after layer completes loading (aka "when")
      zoomLayer.when(async () => {
        const origZoomExtent = useLayer ? zoomLayer.fullExtent : this.demProjectVM.aoiGraphicsLayer.graphics.at(0).geometry.extent;

        await esriProjection.load();
        const zoomExtent = esriProjection.project(origZoomExtent, this.mapView.spatialReference) as Geometry;

        //zooming to viewpoint ensures that rotation orientation is reset back to 0
        const viewpoint = new Viewpoint();
        viewpoint.targetGeometry = zoomExtent;
        viewpoint.rotation = 0;
        this.mapView.goTo(viewpoint);
      });
    } else {
      if (this.countyExtent === undefined) {

        await this.getProjectCountyExtent();
        if (this.countyExtent !== undefined) {
          if (stores.AppStore.efh2Store.homeWidget) {
            stores.AppStore.efh2Store.homeWidget!.viewpoint = new Viewpoint({
              targetGeometry: this.countyExtent,
              rotation: 0,
            });
          }
        }
      }
      if (this.countyExtent !== undefined) {
        //zooming to viewpoint ensures that rotation orientation is reset back to 0
        const viewpoint = new Viewpoint();
        viewpoint.targetGeometry = this.countyExtent;
        viewpoint.rotation = 0;
        this.mapView.goTo(viewpoint);
      }
    }
  }


  private async getProjectCountyExtent(): Promise<void> {
    const project = stores.AppStore.efh2Store.projectsStore.currentProject;
    if (project && project.state !== '') {
      const response = await PRJAPI.StateCountyController.getCountiesByStateUsingGET(project.state);
      if (response.data) {
        const counties = response.data;

        if (project.county === '') {
          // compute state bounding coordinates
          let stateXMin: number | undefined = undefined;
          let stateXMax: number | undefined = undefined;
          let stateYMin: number | undefined = undefined;
          let stateYMax: number | undefined = undefined;
          for (const co of counties) {
            DemIntegrationVM.normalizeCountyXRange(co);
            if ((co.xmin !== undefined) && (stateXMin === undefined || stateXMin > co.xmin)) {
              stateXMin = co.xmin;
            }
            if ((co.xmax !== undefined) && (stateXMax === undefined || stateXMax < co.xmax)) {
              stateXMax = co.xmax;
            }
            if ((co.ymin !== undefined) && (stateYMin === undefined || stateYMin > co.ymin)) {
              stateYMin = co.ymin;
            }
            if ((co.ymax !== undefined) && (stateYMax === undefined || stateYMax < co.ymax)) {
              stateYMax = co.ymax;
            }
          }
          if (
            stateXMin !== undefined &&
            stateXMax !== undefined &&
            stateYMin !== undefined &&
            stateYMax !== undefined
          ) {
            this.countyExtent = new Extent({
              xmin: stateXMin,
              xmax: stateXMax,
              ymin: stateYMin,
              ymax: stateYMax,
              spatialReference: new SpatialReference({ wkid: 4326 }),
            }) as Geometry;
          }
          return;
        }
        const county = counties.find((co) => {
          return co.county_name === project.county;
        });
        if (
          county &&
          county.xmin !== undefined &&
          county.xmax !== undefined &&
          county.ymin !== undefined &&
          county.ymax !== undefined
        ) {
          DemIntegrationVM.normalizeCountyXRange(county);
          this.countyExtent = new Extent({
            xmin: county.xmin,
            xmax: county.xmax,
            ymin: county.ymin,
            ymax: county.ymax,
            spatialReference: new SpatialReference({ wkid: 4326 }),
          }) as Geometry;
        }
      }
    }
  }

  private static normalizeCountyXRange(county: StateCountyDto): void {
    if (
      county.xmin !== undefined &&
      county.xmax !== undefined &&
      county.xmax - county.xmin > 10000000 &&
      county.xmax > 0 &&
      county.xmin < 0
    ) {
      const minX = county.xmax + -20026376.39 * 2.0;
      county.xmax = county.xmin;
      county.xmin = minX;
    }
  }


  public setCellSizeStreamThreshold(cellSize: number, units: GpLinearUnit = 'meters'): void {
    const metersPerUnit = (units === 'meters' ? 1 : 3.2808333333);
    this.currentDemCellSize = cellSize / metersPerUnit;
    if ((this.currentDemCellSize! > 9) && (this.in_stream_threshold < 8)) {
      this.in_stream_threshold = 8;
    } else if ((this.currentDemCellSize! > 4) && (this.in_stream_threshold < 4)) {
      this.in_stream_threshold = 4;
    }
  }


  @action.bound statusCallback(jobInfo: __esri.JobInfo) {
    if (jobInfo && jobInfo.messages && jobInfo.messages.length > 0) {
      const messages = jobInfo.messages
        .map((item) => {
          return item.description;
        })
        .filter((item) => {
          return item.startsWith('#MESSAGE#') || item.startsWith('Executing...') || item.startsWith('Succeeded at ');
        })
        .map((item) => {
          return item.replace('#MESSAGE#', '');
        });

      this.jobMessages = messages;
    }
  }

  @action.bound setDemFile(file) {
    this.in_dem_file = file;
    setStore.upload_dem_file = file;
    this.in_dem_elevation_units = 'feet';
    this.demProjectVM.in_dem_file = this.in_dem_file;
  }

  @action.bound setContoursChoice() {
    if (this.demProjectVM instanceof DelineateWatershedInputStateVM) {
      this.in_generate_contours = !this.in_generate_contours;
      this.demProjectVM.in_generate_contours = this.in_generate_contours;
    } else if (this.demProjectVM instanceof ClippedContainerVM) {
      this.in_generate_contours = !this.in_generate_contours;
    }
  }

  @action.bound setContourInterval = async (value: number) => {
    designStore.contourInterval = value;
    setStore.isContourIntervalEdited = true;
    if (setStore.currentDEM.demSurfaceName) {
      this.saveButtonDisabled = false;
    }
  }

  @action.bound setStreamlinesChoice() {
    if (this.demProjectVM instanceof DelineateWatershedInputStateVM) {
      this.in_generate_streamlines = !this.in_generate_streamlines;
      this.demProjectVM.in_generate_streamlines = this.in_generate_streamlines;
    } else if (this.demProjectVM instanceof ClippedContainerVM) {
      this.in_generate_streamlines = !this.in_generate_streamlines;
    }
  }

  @action.bound setStreamlineThreshold(value) {
    if (this.demProjectVM instanceof DelineateWatershedInputStateVM) {
      this.in_stream_threshold = value;
      this.demProjectVM.in_stream_threshold = this.in_stream_threshold;
    } else if (this.demProjectVM instanceof ClippedContainerVM) {
      this.in_stream_threshold = value;
    }
  }

  @action.bound setHillshadeChoice() {
    if (this.demProjectVM instanceof DelineateWatershedInputStateVM) {
      this.in_generate_hillshade = !this.in_generate_hillshade;
      this.demProjectVM.in_generate_hillshade = this.in_generate_hillshade;
    } else if (this.demProjectVM instanceof ClippedContainerVM) {
      this.in_generate_hillshade = !this.in_generate_hillshade;
    }
  }

  @action.bound setDepthgridChoice() {
    if (this.demProjectVM instanceof DelineateWatershedInputStateVM) {
      this.in_generate_depthgrid = !this.in_generate_depthgrid;
      this.demProjectVM.in_generate_depthgrid = this.in_generate_depthgrid;
    } else if (this.demProjectVM instanceof ClippedContainerVM) {
      this.in_generate_depthgrid = !this.in_generate_depthgrid;
    }
  }

  @action.bound disableServiceDems() { // Turns off visible service DEM layers
    if (this.bestAvailableHillshade) this.bestAvailableHillshade.visible = false;
    this.demServicesGroupLayer.layers.forEach((lyr) => (lyr.visible = false));
    this.in_dem_hillshade = "None";
  }

  @action.bound clearAvailableDemServices() {
    this.availableDemServices.splice(0);
    this.isDemServiceAvailable = false;
    this.in_dem_id = '';
    this.in_dem_service_url = '';
    this.in_dem_title = '';
  }

  @action.bound clearAvailableNaipServices() {
    this.availableNaipServices = undefined;
    this.isNaipServiceAvailable = false;
  }

  @action.bound clearDemGraphics() {
    this.in_dem_file = undefined;
    this.demProjectVM.in_dem_file = this.in_dem_file;
    this.demProjectVM.result_dem = undefined;
    this.demProjectVM.result_dem_boundary = undefined;
    this.demProjectVM.resultsGroupLayer.removeAll();
  }

  @action.bound async selectDemService() {
    const demService = this.configDemServices.find((ds) => {
      return this.in_dem_id === ds.id;
    });
    if (demService) {
      this.in_dem_service_url = demService.url;
      this.in_dem_elevation_units = demService.units;
      this.in_dem_metadata_url = demService.metadata;
      if (demService.id === "UsdaBareEarth10m") {
        this.checkUsdaBareEarth10m();
      }
      this.in_dem_title = demService.title;
      this.in_dem_hillshade = demService.hillshadeName;
      this.saveButtonDisabled = false;
      setStore.isImportingNewDEM = true;
    } else {
      this.in_dem_service_url = '';
      this.in_dem_elevation_units = '';
      this.in_dem_metadata_url = '';
      this.in_dem_title = '';
      this.in_dem_hillshade = '';
      this.saveButtonDisabled = true;
      setStore.isImportingNewDEM = false;
    }
  }

  @action.bound async tryDemServicesAvailability() {
    if (this.hasAoiGraphics) {
      await this.checkDemServicesAvailability();
    }
  }

  @action.bound async checkDemServicesAvailability(useDynamicClipSize: boolean = false) {
    if (this.suppressDemServiceLoading) {
      // this.suppressDemServiceLoading = false;
      return;
    }
    this.suppressDemServiceLoading = true;
    this.clearAvailableDemServices();
    if (this.hasAoiGraphics) {
      const graphic = this.demProjectVM.aoiGraphicsLayer.graphics.getItemAt(0);
      if (graphic) {
        //cancel any outstanding drawing inputs
        this.aoiSketch.cancel();
        await sleep(100); //introduced sleep to give sketch.cancel operation time to complete before disabling sketches,
        this.disableSketchControls();

        const aoi = graphic.geometry as Polygon;
        const centroid = aoi.centroid;

        const aoiUtm = await DemIntegrationVM.getAoiBBoxInUtm(aoi);

        for (const imgLyr of this.demServicesGroupLayer.layers) {
          if (!(imgLyr.title.endsWith(__notAvailableSuffix))) {
            try {
              const imageUrl = (imgLyr as ImageryLayer).url;
              const identifyResult = await this.identifyDemValue(imageUrl, centroid);
              if (identifyResult !== undefined) {
                if ((identifyResult.value !== undefined) && (identifyResult.value !== 'NoData')) {
                  const numValue = Number(identifyResult.value);
                  if (!(isNaN(numValue))) {
                    if (numValue > -9990) {
                      const found = this.configDemServices.find((s) => s.id === imgLyr.id);
                      if (found) {
                        let demServiceProps = this.demServicesProperties.find((dsp) => { return dsp.id === found.id; });
                        if (!demServiceProps) {
                          demServiceProps = await this.getDemServiceProperties(imageUrl);
                          if (demServiceProps && demServiceProps.spatialReference && demServiceProps.pixelSizeX) {
                            this.demServicesProperties.push({
                              ...demServiceProps,
                              cellSize: DemIntegrationVM.getDemServiceCellSize(demServiceProps),
                              id: found.id as string,
                              title: imgLyr.title,
                              url: imageUrl
                            });
                          }
                        }
                        if (demServiceProps && demServiceProps.spatialReference && demServiceProps.pixelSizeX) {
                          const availableDemService = DemIntegrationVM.getAvailableDemService(demServiceProps, aoiUtm, useDynamicClipSize);
                          this.availableDemServices.push(availableDemService);
                          this.isDemServiceAvailable = true;
                        }
                      }
                    }
                  }
                }
              }
            } catch (error) {
              console.warn(`${imgLyr.id} has error!`);
            }
          }
        }
        this.enableSketchControls();
        if (!this.isDemServiceAvailable) {
          await MessageCenter.addWarningFromCode('Hydro_54', [
            {
              callback: () => {
                MessageCenter.clearMessages();
                this.cancel();
              },
              text: 'Cancel',
            },
          ]);
        }
      }

      // this.availableDemServices = this.configDemServices.map((i) => i);
    } else {
      this.in_dem_service_selection = undefined;
      await MessageCenter.addWarningFromCode('Hydro_71', [
        {
          callback: () => {
            MessageCenter.clearMessages();
            this.cancel();
          },
          text: 'Cancel',
        },
      ]);
    }
    this.suppressDemServiceLoading = false;
  }

  @action.bound async tryNaipServicesAvailability() {
    if (this.hasAoiGraphics) {
      await this.checkNaipServicesAvailability();
    }
  }

  @action.bound async checkNaipServicesAvailability() {
    if (this.suppressNaipServiceLoading) return;
    this.suppressNaipServiceLoading = true;

    if (!this.configNaipService) {
      this.in_naip_selection = undefined;
      this.suppressNaipServiceLoading = false;
      return;
    }

    if (!this.hasAoiGraphics) {
      this.in_naip_selection = undefined;
      await MessageCenter.addWarningFromCode('Hydro_71', [
        {
          callback: () => {
            MessageCenter.clearMessages();
            this.cancel();
          },
          text: 'Cancel',
        },
      ]);
      this.suppressNaipServiceLoading = false;
      return;
    }

    const graphic = this.demProjectVM.aoiGraphicsLayer.graphics.getItemAt(0);
    if (!graphic) {
      this.in_naip_selection = undefined;
      this.suppressNaipServiceLoading = false;
      return;
    }

    // Cancel any outstanding drawing inputs
    this.aoiSketch.cancel();
    await sleep(100); // Wait for sketch.cancel to take effect
    this.disableSketchControls();

    const aoi = graphic.geometry as Polygon;
    const centroid = aoi.centroid;

    try {
      const identifyResult = await this.identifyDemValue(this.configNaipService, centroid, 1);
      let isValid = false;
      if (identifyResult?.value !== undefined && identifyResult.value !== 'NoData') {
        const valueArray = identifyResult.value.split(',').map((v: string) => (v.trim())).filter((v: number) => !isNaN(v));
        if (valueArray.length >= 3 && valueArray.some(v => v > 0 && v <= 255)) {
          isValid = true;
        };
      };
      this.isNaipServiceAvailable = isValid;

    } catch (error) {
      console.warn('NAIP service check failed:', error);
      this.isNaipServiceAvailable = false;
    }

    this.enableSketchControls();

    if (!this.isNaipServiceAvailable) {
      this.in_naip_selection = undefined;
      await MessageCenter.addWarning('NAIP imagery is not available in the selected area.', [
        {
          callback: () => {
            MessageCenter.clearMessages();
            this.cancel();
          },
          text: 'Cancel',
        },
      ]);
    }
    this.suppressNaipServiceLoading = false;
  }

  @action.bound openDemWindow() {
    const left = screen.width / 2 - 850 / 2 + this.findLeftWindowBoundry();
    const top = screen.height / 2 - 500 / 2 + this.findTopWindowBoundry();
    const win = window.open('', '_blank', `width= 850, height=500, top=${top}, left=${left}`);
    if (win) {
      win.document.title = `Metadata: ${this.in_dem_title}`;
      win.document.write("<!DOCTYPE html>");
      win.document.write("<html>");
      win.document.write("<head>");
      win.document.write("</head>");
      win.document.write("<body>");
      win.document.write("</body>");
      win.document.write("</body>");
      win.document.body.classList.add("external-window-scroll")

      const parentHead = window.document.querySelector("head")?.childNodes;
      parentHead?.forEach(item => {
        win.document.head.appendChild(item.cloneNode(true));
      })

      const demMetadataMarkup = win.document.createElement("div");
      win.document.body.appendChild(demMetadataMarkup);

      const header = win.document.createElement("h1");
      header.classList.add('metadata-h1');
      header.innerHTML = `Metadata: ${this.in_dem_title}`;
      demMetadataMarkup.appendChild(header);

      const table = win.document.createElement("table");
      table.classList.add('dem-metadata-table');
      demMetadataMarkup.appendChild(table);

      const thead = win.document.createElement('thead');
      table.appendChild(thead);

      const theadtr = win.document.createElement('tr');
      try {
        for (const key of Object.keys(this.demMetadataValues[0])) {
          const field = win.document.createElement('th');
          field.innerHTML = key;
          theadtr.appendChild(field);
        }
        thead.appendChild(theadtr);

        const tbody = win.document.createElement('tbody');
        table.appendChild(tbody);

        this.demMetadataValues.forEach((entry) => {
          const tabler = win.document.createElement('tr');
          for (const value of Object.values(entry)) {
            const metadataValue = (value === null) ? '' : String(value).trim();
            if (metadataValue.includes('https')) {
              const valueElement = win.document.createElement('a');
              valueElement.innerHTML = metadataValue;
              valueElement.href = metadataValue;
              valueElement.target = "_blank";
              tabler.appendChild(valueElement);
            } else {
              const valueElement = win.document.createElement('td');
              valueElement.innerHTML = metadataValue as any;
              tabler.appendChild(valueElement);
            }
            tbody.appendChild(tabler);
          }
        })
      }
      catch {
        console.log('Issue displaying metadata table')
      }
    }
  }

  @action.bound findLeftWindowBoundry() {
    if (window.screenLeft) {
      return window.screenLeft;
    }
    if (window.screenX) {
      return window.screenX;
    }

    return 0;
  }

  @action.bound findTopWindowBoundry() {
    if (window.screenTop) {
      return window.screenTop;
    }
    if (window.screenY) {
      return window.screenY;
    }

    return 0;
  }

  @action.bound openDemUrl() {
    if (this.in_dem_id) {
      if (this.in_dem_metadata_url) {
        const featureLayer = new FeatureLayer({
          url: this.in_dem_metadata_url
        });
        const query = new Query();
        query.spatialRelationship = 'intersects';
        query.where = '1=1';
        query.outFields = ['*'];
        query.geometry = this.demProjectVM.aoiGraphicsLayer.graphics.at(0).geometry;

        featureLayer.queryFeatures(query)
          .then(
            ((featureSet) => {
              const features = featureSet.features;
              this.demMetadataValues = [...features].map(feature => {
                return feature.attributes;
              });
              if (this.demMetadataValues[0] !== undefined) {
                this.openDemWindow()
              }
              else {
                MessageCenter.addWarningFromCode('Hydro_74');
                this.openCenteredWindow();
              }
            }),
            () => {
              MessageCenter.addWarningFromCode('Hydro_66');
              this.openCenteredWindow();
            }
          );
      } else {
        this.openCenteredWindow();
      }
    } else {
      MessageCenter.addWarningFromCode('Hydro_56');
    }
  }

  private openCenteredWindow() {
    const left = screen.width / 2 - 850 / 2 + this.findLeftWindowBoundry();
    const top = screen.height / 2 - 500 / 2 + this.findTopWindowBoundry();
    window.open(`${this.in_dem_service_url}/view/Metadata`, '_blank', `width= 850, height=500, top=${top}, left=${left}`);
  }

  @action.bound async demClip(): Promise<boolean> {
    //TODO: This function is solely for Design Container, need some refactors to integrate to Hydrology
    let result: boolean = false;
    //wait till creating a DEM surface is done
    if (this.jobExecuting) {
      await MessageCenter.addWarningFromCode('Hydro_72', [
        {
          callback: () => {
            MessageCenter.clearMessages();
            this.cancel();
          },
          text: 'Cancel',
        },
      ]);
      return result;
    }
    //pop up warning message when there is no available dem service
    if (this.in_dem_service_url.length < 1) {
      await MessageCenter.addWarningFromCode('Hydro_65', [
        {
          callback: () => {
            MessageCenter.clearMessages();
            this.cancel();
          },
          text: 'Cancel',
        },
      ]);
      return result;
    }
    //user has to draw AOI first before trigger the DEM Clip function.
    if (!(this.demProjectVM.aoiGraphicsLayer && 0 < this.demProjectVM.aoiGraphicsLayer.graphics.length)) {
      await MessageCenter.addWarningFromCode('Hydro_71', [
        {
          callback: () => {
            MessageCenter.clearMessages();
            this.cancel();
          },
          text: 'Cancel',
        },
      ]);
      return result;
    }
    const aoiGeometry = this.demProjectVM.aoiGraphicsLayer.graphics.getItemAt(0).geometry;

    this.demFileCellSize = undefined;

    //cancel any outstanding drawing inputs
    this.aoiSketch.cancel();
    await sleep(100); //introduced sleep to give sketch.cancel operation time to complete before disabling sketches,
    this.disableSketchControls();

    try {
      this.jobExecuting = true;
      this.jobMessages = ['Clipping DEM...'];

      const selectedDemService = this.availableDemServices.find((ds) => {
        return this.in_dem_id === ds.id;
      });
      if (selectedDemService) {
        if (selectedDemService.notEligible) {
          await MessageCenter.addNotificationFromCode('Hydro_50', [
            {
              callback: () => {
                MessageCenter.clearMessages();
                this.cancel();
              },
              text: 'Cancel',
            },
          ]);
        } else {
          const demProps = await this.clipDemFromService(selectedDemService);
          if (undefined !== demProps && undefined !== demProps.href) {
            this.selected_dem_service_url = selectedDemService.url;
            await sleep(2000);
            // the following code ensures the clipping is really complete, thus elimninates the error that occurs in clipping process.
            // also ensures wherever the ArcGIS server resides, the USDA DEM clip always works.
            // now the clipping process is stabler.
            let demRetrieved = false;
            for (let i = 0; i < 60; i++) {
              try {
                await sleep(2000); // repeatedly wait until the image export completes, and get a blob from the url.
                const resBlob = await fetch(demProps.href).then((r) => r.blob());
                if (resBlob.type === 'image/tiff') {
                  const index = (demProps.href as string).lastIndexOf('/');
                  const fileName = (demProps.href as string).substring(index + 1);
                  const file = new File([resBlob], fileName, { type: 'image/tiff' });
                  this.in_dem_file = file;
                  this.demProjectVM.result_dem = undefined;
                  demRetrieved = true;
                  break;
                }
              } catch {
                // suppress the error
              }
            }
            if (demRetrieved) {
              this.checkInputDemUnit(aoiGeometry);
              this.in_dem_clip_url = demProps.href;
              result = true;
            } else {
              await MessageCenter.addErrorFromCode('Hydro_73', [
                {
                  callback: () => {
                    MessageCenter.clearMessages();
                    this.cancel();
                  },
                  text: 'Cancel',
                },
              ]);
            }
          } else {
            await MessageCenter.addErrorFromCode('Hydro_60', [
              {
                callback: () => {
                  MessageCenter.clearMessages();
                  this.cancel();
                },
                text: 'Cancel',
              },
            ]);
          }
        }
      } else {
        await MessageCenter.addErrorFromCode('Hydro_60', [
          {
            callback: () => {
              MessageCenter.clearMessages();
              this.cancel();
            },
            text: 'Cancel',
          },
        ]);
      }
    } catch (e) {
      await MessageCenter.addErrorFromCode('Hydro_60', [
        {
          callback: () => {
            MessageCenter.clearMessages();
            this.cancel();
          },
          text: 'Cancel',
        },
      ]);
    } finally {
      this.jobExecuting = false;
      this.enableSketchControls();
    }
    return result;
  }


  @action.bound async clipNaipImagery(): Promise<any> {
    return await this.clipNaipImageryFromService(this.configNaipService);
  }

  private async clipDemFromService(demServiceProps: IAvailableDemService) {
    let result: any = undefined;

    const clipQueryUrl = demServiceProps.url + '/exportImage';
    const bBox = `${demServiceProps.bBoxXMin},${demServiceProps.bBoxYMin},${demServiceProps.bBoxXMax},${demServiceProps.bBoxYMax}`;
    const imageSize = `${demServiceProps.width},${demServiceProps.height}`;
    const spatialReference = libraryStore.projectSpatialReference!;
    let input = '';

    if (spatialReference && spatialReference.wkid) {
      input = `${spatialReference.wkid}`;
    }
    else if (spatialReference && spatialReference.wkt) {
      input = JSON.stringify(spatialReference);
    }

    const clipQueryParams = {
      bbox: bBox,
      bboxSR: demServiceProps.crs,
      size: imageSize,
      imageSR: input,
      format: 'tiff',
      pixelType: demServiceProps.pixelType,
      noData: -9999,
      noDataInterpretation: 'esriNoDataMatchAny',
      interpolation: 'RSP_BilinearInterpolation', // 'RSP_NearestNeighbor',
      compression: 'LZ77',
      bandIds: '0',
      f: 'json'
    };

    // console.warn('CLIP PARAMETERS');
    // console.warn(clipQueryParams);

    const maxTries = 10;
    let counter = 0;
    while (counter < maxTries) {
      await esriRequest(clipQueryUrl, {
        method: 'post',
        query: clipQueryParams,
        responseType: 'json',
        timeout: demServiceTimeout,
      })
        .then((response) => {
          if (response.data) {
            result = response.data;
          }
        })
        .catch((error) => {
          result = undefined;
        });
      if (result !== undefined) {
        break;
      }
      counter++;
    }
    return result;
  }

  private async clipNaipImageryFromService(naipServiceUrl: string): Promise<any> {
    let result: any = undefined;

    if (!this.hasAoiGraphics || !this.demProjectVM.aoiGraphicsLayer.graphics.length) {
      return undefined;
    }

    const graphic = this.demProjectVM.aoiGraphicsLayer.graphics.getItemAt(0);
    const aoi = graphic.geometry as Polygon;

    // Get NAIP service properties to understand service configuration
    const naipServiceProps = await this.getNaipServiceProperties(naipServiceUrl);
    if (!naipServiceProps) {
      return undefined;
    }

    // Project AOI to the NAIP service's spatial reference (Web Mercator 3857)
    const naipSpatialRef = new SpatialReference({
      wkid: naipServiceProps.spatialReference?.latestWkid || naipServiceProps.spatialReference?.wkid || 3857
    });

    await esriProjection.load();
    const aoiInServiceSR = esriProjection.project(aoi, naipSpatialRef) as Polygon;
    if (!aoiInServiceSR) {
      console.error('Failed to project AOI to NAIP service spatial reference');
      return undefined;
    }

    const aoiExtent = aoiInServiceSR.extent;
    const clipQueryUrl = naipServiceUrl + '/exportImage';

    // Use the service's native spatial reference for bounding box
    const bBox = `${aoiExtent.xmin},${aoiExtent.ymin},${aoiExtent.xmax},${aoiExtent.ymax}`;

    // Calculate appropriate image size based on service pixel size and AOI extent
    const pixelSizeX = naipServiceProps.pixelSizeX || 1;
    const pixelSizeY = naipServiceProps.pixelSizeY || 1;

    // Calculate width and height in pixels, respecting service limits
    const maxWidth = naipServiceProps.maxImageWidth || 4100;
    const maxHeight = naipServiceProps.maxImageHeight || 4100;

    const extentWidth = aoiExtent.xmax - aoiExtent.xmin;
    const extentHeight = aoiExtent.ymax - aoiExtent.ymin;

    let width = Math.ceil(extentWidth / pixelSizeX);
    let height = Math.ceil(extentHeight / pixelSizeY);

    // Ensure we don't exceed service limits
    if (width > maxWidth) {
      width = maxWidth;
    }
    if (height > maxHeight) {
      height = maxHeight;
    }

    // Ensure minimum size for visible image
    if (width < 100) width = 100;
    if (height < 100) height = 100;

    const imageSize = `${width},${height}`;

    // Get output spatial reference (project's spatial reference)
    const spatialReference = libraryStore.projectSpatialReference!;
    let imageSR = '';

    if (spatialReference && spatialReference.wkid) {
      imageSR = `${spatialReference.wkid}`;
    } else if (spatialReference && spatialReference.wkt) {
      imageSR = JSON.stringify(spatialReference);
    } else {
      // Fallback to service's native spatial reference
      imageSR = `${naipSpatialRef.wkid}`;
    }

    const bandIds = '0,1,2,3'; // Always use RGB for color imagery

    // Correct NAIP export parameters based on service metadata
    const clipQueryParams = {
      bbox: bBox,
      bboxSR: naipSpatialRef.wkid,
      size: imageSize,
      imageSR: imageSR,
      format: 'tiff',
      pixelType: 'U8',
      interpolation: 'RSP_NearestNeighbor',
      compression: '',
      bandIds: bandIds,
      noData: '',
      noDataInterpretation: 'esriNoDataMatchAny',
      adjustAspectRatio: true,
      f: 'json'
    };


    const maxTries = 10;
    let counter = 0;
    while (counter < maxTries) {
      try {
        const response = await esriRequest(clipQueryUrl, {
          method: 'post',
          query: clipQueryParams,
          responseType: 'json',
          timeout: demServiceTimeout,
        });

        if (response.data && response.data.href) {
          result = response.data;
          break;
        } else if (response.data && response.data.error) {
          console.error('NAIP export error:', response.data.error);
          break;
        }
      } catch (error) {
        console.warn(`NAIP clip attempt ${counter + 1} failed:`, error);

        // If it's a size-related error, try reducing the image size
        if ((error as Error).message && (error as Error).message.includes('size') && counter < 3) {
          const newWidth = Math.floor(width * 0.8);
          const newHeight = Math.floor(height * 0.8);
          clipQueryParams.size = `${newWidth},${newHeight}`;
        }

        result = undefined;
      }
      counter++;
    }

    return result;
  }

  private checkInputDemUnit(aoiGeometry: __esri.Geometry) {
    if (aoiGeometry && this.in_dem_elevation_units === "various" && this.in_dem_metadata_url) {
      this.in_dem_elevation_units = "meters";

      const featureLayer = new FeatureLayer({ url: this.in_dem_metadata_url });
      const query = new Query();
      query.spatialRelationship = 'intersects';
      query.where = '1=1';
      query.outFields = ['*'];
      query.geometry = this.demProjectVM.aoiGraphicsLayer.graphics.at(0).geometry;

      featureLayer.queryFeatures(query)
        .then((featureSet) => {
          if (featureSet.features.length > 0) {
            const feat = featureSet.features[0];
            const unit: string = feat.attributes.VertUnit;
            // console.log(`metadata VertUnit: ${unit}`);
            if (unit && (unit.toLowerCase().includes('feet') || unit.toLowerCase().includes('ft'))) {
              this.in_dem_elevation_units = 'feet';
            }
          }
        });
    }
  }

  private async checkAoiTooLarge(): Promise<boolean> {
    let result = false;

    if (this.demProjectVM.aoiGraphicsLayer && (this.demProjectVM.aoiGraphicsLayer.graphics.length > 0)) {
      if (this.demFileCellSize === undefined) {
        if (this.demProjectVM.result_dem_boundary && (this.demProjectVM.result_dem_boundary.features.length > 0)) {
          const feature = this.demProjectVM.result_dem_boundary.features[0];
          if (feature !== undefined) {
            this.demFileCellSize = feature.attributes['CellSize'];
            // console.warn('FILE CELL SIZE:', this.demFileCellSize);
          }
        }
      }
      if ((this.demFileCellSize !== undefined) && (this.demProjectVM.workingSpatialReference !== undefined)) {
        let aoiGeometry = this.demProjectVM.aoiGraphicsLayer.graphics.at(0).geometry;
        if (!aoiGeometry.spatialReference.equals(this.demProjectVM.workingSpatialReference)) {
          await esriProjection.load();
          aoiGeometry = esriProjection.project(this.demProjectVM.aoiGraphicsLayer.graphics.at(0).geometry, this.demProjectVM.workingSpatialReference) as Geometry;
        }
        const aoiExtent = aoiGeometry.extent;
        const widthCells = Math.ceil((aoiExtent.xmax - aoiExtent.xmin) / this.demFileCellSize);
        const hightCells = Math.ceil((aoiExtent.ymax - aoiExtent.ymin) / this.demFileCellSize);
        // console.log('AOI SIZE:', widthCells, hightCells);
        // using an empirical coefficient of 1.3 to account for AOI buffering
        if (demClipMaxSize < widthCells * hightCells /* * 1.3 */) {
          result = true;
        }
      }
    }

    return result;
  }

  private async aoiSketchUpdateHandler(e: __esri.SketchUpdateEvent) {
    // console.warn(this.aoiModified ? 'MODIFIED' : 'UUUUUUUUUUUUUU');
    // console.warn('AOI EVENT:', e.state);
    if (e.graphics[0] !== null) {
      const area = esriGeometryEngine.geodesicArea(e.graphics[0].geometry as Polygon, "acres");
      this.aoiArea = area.toFixed(2);
    }
    if (this.in_dem_service_selection === 'service') {
      if ((e.state === 'complete')) {
        if (this.aoiModified) await this.checkDemServicesAvailability();
      } else if (e.state !== 'start') {
        this.clearAvailableDemServices();
      }
    }
    if (e.state !== 'start' && !this.aoiModified) {
      // console.log('AOI Sketch Update Handler');
      this.aoiModified = true;
    }
  }

  private disableSketchControls() {
    this.aoiSketch.layer = undefined as any;
    this.aoiSketch.view = undefined as any;
    if (this.demProjectVM instanceof DelineateWatershedInputStateVM) {
      this.demProjectVM.culvertSketch.layer = undefined as any;
      this.demProjectVM.culvertSketch.view = undefined as any;
      this.demProjectVM.outletSketch.layer = undefined as any;
      this.demProjectVM.outletSketch.view = undefined as any;
      this.demProjectVM.watershedSketch.layer = undefined as any;
      this.demProjectVM.watershedSketch.view = undefined as any;
    }
  }

  private enableSketchControls() {
    this.aoiSketch.layer = this.demProjectVM.aoiGraphicsLayer;
    this.aoiSketch.view = this.mapView;
    if (this.demProjectVM instanceof DelineateWatershedInputStateVM) {
      this.demProjectVM.culvertSketch.layer = this.demProjectVM.culvertGraphicsLayer;
      this.demProjectVM.culvertSketch.view = this.mapView;
      this.demProjectVM.outletSketch.layer = this.demProjectVM.outletGraphicsLayer;
      this.demProjectVM.outletSketch.view = this.mapView;
      this.demProjectVM.watershedSketch.layer = this.demProjectVM.watershedGraphicsLayer;
      this.demProjectVM.watershedSketch.view = this.mapView;
    }
  }

  private resolveProceedWithValues?: (value: boolean) => void;

  @action.bound cancel(): void {
    if (this.resolveProceedWithValues) {
      //resolve the promise
      this.resolveProceedWithValues(false);

      //discard callback handle
      this.resolveProceedWithValues = undefined;
    }
  }

  @computed get hasAoiGraphics(): boolean {
    return this.demProjectVM.aoiGraphicsLayer.graphics.length > 0;
  }

  private async getDemServiceProperties(demURL: string) {
    let result;
    await esriRequest(demURL + '?f=json', {
      responseType: 'json',
    })
      .then((response) => {
        if (response.data) {
          result = response.data;
        }
      })
      .catch((error) => {
        result = undefined;
      });
    return result;
  }

  private async getNaipServiceProperties(naipServiceUrl: string): Promise<any> {
    try {
      const response = await esriRequest(naipServiceUrl, {
        query: { f: 'json' },
        responseType: 'json',
        timeout: demIdentifyTimeout,
      });

      if (response.data) {
        return {
          ...response.data,
          crs: response.data.spatialReference?.latestWkid || response.data.spatialReference?.wkid || 4326,
          pixelSizeX: response.data.pixelSizeX || 1,
          pixelSizeY: response.data.pixelSizeY || 1,
          maxImageWidth: response.data.maxImageWidth || 4096,
          maxImageHeight: response.data.maxImageHeight || 4096,
          bandCount: response.data.bandCount || 4, // NAIP typically has 4 bands (R,G,B,NIR)
          pixelType: response.data.pixelType || 'U8',
        };
      }
    } catch (error) {
      console.error('Failed to get NAIP service properties:', error);
    }
    return undefined;
  }

  private async identifyDemValue(demServiceUrl: string, point: Point, maxTry: number = 10) {
    let result;

    const identifyUrl = demServiceUrl + '/identify';
    const identifyParams = {
      geometry: point,
      geometryType: 'esriGeometryPoint',
      returnGeometry: false,
      returnCatalogItems: false,
      returnPixelValues: false,
      f: 'json'
    }

    for (let i = 0; i < maxTry; i++) {
      await esriRequest(identifyUrl, {
        method: 'post',
        query: identifyParams,
        responseType: 'json',
        timeout: demIdentifyTimeout,
      }).then((response) => {
        if (response.data) {
          result = response.data;
        }
      }).catch((error) => {
        console.warn('DEM IMAGE IDENTIFY ERROR:', identifyUrl, error.message);
      });
      if (result) {
        break;
      }
    }
    return result;
  }

  private static getDemServiceCellSize(demServiceProps): number {
    const metersPerUnit = (demServiceProps.spatialReference.wkid === 4326 ? 111319.490793274 : 1);
    return (demServiceProps.pixelSizeX * metersPerUnit);
  }

  private static async getAoiBBoxInUtm(aoi: Polygon): Promise<IAoiBBoxInUtm | undefined> {
    let result: IAoiBBoxInUtm | undefined = undefined;
    if (aoi && aoi.spatialReference) {
      const utmWkid = await gisUtils.getUtmZoneWkid(aoi.centroid);
      if (((utmWkid > 32600) && (utmWkid < 32661)) || ((utmWkid > 32700) && (utmWkid < 32761))) {
        const utmCrs = new SpatialReference({ wkid: utmWkid });
        if (!esriProjection.isLoaded()) {
          await esriProjection.load();
        }
        const aoiUtm = esriProjection.project(aoi, utmCrs) as Polygon;
        if (aoiUtm) {
          const aoiExtent = aoiUtm.extent;
          const halfWidth = (aoiExtent.xmax - aoiExtent.xmin) / 2.0;
          const halfHeight = (aoiExtent.ymax - aoiExtent.ymin) / 2.0;
          result = {
            crs: utmCrs,
            centerX: aoiExtent.xmin + halfWidth,
            centerY: aoiExtent.ymin + halfHeight,
            halfWidth: halfWidth,
            halfHeight: halfHeight
          };
        }
      }
    }
    return result;
  }

  private static getAvailableDemService(demServiceProps: any, aoiUtm?: IAoiBBoxInUtm, useDynamicClipSize: boolean = false): IAvailableDemService {
    const result: IAvailableDemService = {
      id: demServiceProps.id as string,
      title: demServiceProps.title as string,
      notEligible: true
    };
    if (aoiUtm) {
      const cellHalfWidth = Math.ceil(aoiUtm.halfWidth / demServiceProps.cellSize);
      const cellHalfHeight = Math.ceil(aoiUtm.halfHeight / demServiceProps.cellSize);
      const width = cellHalfWidth * 2;
      const height = cellHalfHeight * 2;

      const clipSizeLimit = useDynamicClipSize && demServiceProps.maxImageWidth && demServiceProps.maxImageHeight
        ? demServiceProps.maxImageWidth * demServiceProps.maxImageHeight
        : demClipMaxSize;

      if (((width * height) <= clipSizeLimit) && (width <= demServiceProps.maxImageWidth) && (height <= demServiceProps.maxImageHeight)) {
        result.notEligible = false;
        result.url = demServiceProps.url as string;
        result.pixelType = demServiceProps.pixelType as string;
        result.cellSize = demServiceProps.cellSize as number;
        result.width = width;
        result.height = height;
        result.crs = aoiUtm.crs;

        const imgHalfWidth = cellHalfWidth * demServiceProps.cellSize;
        const imgHalfHeight = cellHalfHeight * demServiceProps.cellSize;
        result.bBoxXMin = aoiUtm.centerX - imgHalfWidth;
        result.bBoxYMin = aoiUtm.centerY - imgHalfHeight;
        result.bBoxXMax = aoiUtm.centerX + imgHalfWidth;
        result.bBoxYMax = aoiUtm.centerY + imgHalfHeight;
      }
    }
    return result;
  }

  @action.bound onMount(history) {
    if (!designStore.hasProject()) {
      MessageCenter.addWarning(designStore.noProjectMessage, [
        {
          callback: () => {
            history?.push('/projects/new-project');
            MessageCenter.clearMessages();
          },
          text: 'Go Create a Project',
        },
      ]);
    }
    if (this.demProjectVM instanceof DesignContainerVM || this.demProjectVM instanceof ClippedContainerVM) {
      this.demProjectVM.aoiSketch.layer = this.demProjectVM.aoiGraphicsLayer;
      this.demProjectVM.aoiSketch.view = this.demProjectVM.view as MapView;
    }
  }

  @action.bound onUnmount() {
    this.demProjectVM.aoiSketch.cancel();
    if (this.demProjectVM instanceof DesignContainerVM || this.demProjectVM instanceof ClippedContainerVM) {
      this.demProjectVM.aoiSketch.layer = undefined as any;
      this.demProjectVM.aoiSketch.view = undefined as any;
      this.unmountLayers();
    }
  }

  @action.bound callableonUnmount() {
    this.onUnmount();
  }

  private mountLayers(): void {
    if (this.demProjectVM instanceof DesignContainerVM || this.demProjectVM instanceof ClippedContainerVM) {
      this.demProjectVM.view?.map.add(this.demServicesGroupLayer);
      this.demProjectVM.view?.map.add(this.demProjectVM.resultsGroupLayer);
      this.demProjectVM.view?.map.add(this.demProjectVM.sketchGroupLayer);
    }
  }

  public unmountLayers(): void {
    if (this.demProjectVM instanceof DesignContainerVM || this.demProjectVM instanceof ClippedContainerVM) {
      this.demProjectVM.view?.map?.remove(this.demServicesGroupLayer);
      this.demProjectVM.view?.map?.remove(this.demProjectVM.resultsGroupLayer);
      this.demProjectVM.view?.map?.remove(this.demProjectVM.sketchGroupLayer);
      this.demProjectVM.view?.map?.remove(this.demProjectVM.aoiGraphicsLayer);
    }
  }

  @action.bound async checkUsdaBareEarth10m() {
    await MessageCenter.addWarningFromCode('DEM_01', [
      {
        callback: () => {
          MessageCenter.clearMessages();
          this.cancel();
        },
        text: 'Cancel',
      },
    ]);
  }

}
interface IAoiBBoxInUtm {
  crs: SpatialReference;
  centerX: number;
  centerY: number;
  halfWidth: number;
  halfHeight: number;
}

interface IAvailableDemService {
  id: string;
  title: string;
  notEligible: boolean;
  url?: string,
  pixelType?: string,
  cellSize?: number,
  width?: number,
  height?: number,
  bBoxXMin?: number,
  bBoxYMin?: number,
  bBoxXMax?: number,
  bBoxYMax?: number,
  crs?: SpatialReference
}

export const removeOriginalDEMLayers = () => {
  const map = EngineeringMapService.instance?.mapView?.map;
  const originalDEMGroup = map?.allLayers.filter((layer) => {
    return layer.title === 'Selected Dem Layer';
  });
  if (originalDEMGroup) {
    for (const lyr of originalDEMGroup) {
      map?.remove(lyr);
    }
  }
  const originalContours = map?.findLayerById(AppConstants.OriginalContourLayerId);
  if (originalContours) map?.remove(originalContours);
  const designContours = map?.findLayerById(AppConstants.DesignedContourLayerId);
  if (designContours) map?.remove(designContours);
  designStore.currentSurface.borderVector = [];
}